import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Lock, Mail, ShieldCheck, ArrowLeft, Eye, EyeOff, AlertCircle } from '../../components/ShopIcons';
import { safeParseJson } from '../../utils/auth';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const role = localStorage.getItem('userRole');
    if (token && role === 'admin') {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError('Please enter both your administrator email and password.');
      return;
    }

    setLoading(true);
    try {
      let authenticated = false;
      let apiError = '';

      try {
        const res = await fetch('/best3intennis-api/api/admin_login.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmedEmail, password })
        });
        const text = await res.text();
        const data = safeParseJson(text);

        const jwtToken = data && (data.token || data.accessToken);
        if (res.ok && data && data.success && jwtToken) {
          localStorage.setItem('accessToken', jwtToken);
          localStorage.setItem('token', jwtToken);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem(
            'adminUser',
            JSON.stringify(
              data.user || {
                email: trimmedEmail,
                name: 'Store Administrator',
                role: 'admin'
              }
            )
          );
          authenticated = true;
        } else if (res.status !== 404 && data && data.message) {
          apiError = data.message;
        }
      } catch (_netErr) {
        // Network/local dev fallback below
      }

      // Local dev fallback before FTP upload for seeded admin credentials
      if (!authenticated && !apiError) {
        if (trimmedEmail === 'dimabot@gmail.com' && password === 'ADad25!') {
          const fakeAdminToken = `local-admin-jwt-${Date.now()}`;
          localStorage.setItem('accessToken', fakeAdminToken);
          localStorage.setItem('userRole', 'admin');
          localStorage.setItem(
            'adminUser',
            JSON.stringify({
              id: 1,
              email: 'dimabot@gmail.com',
              name: 'Store Administrator',
              role: 'admin'
            })
          );
          authenticated = true;
        } else {
          apiError = 'Invalid administrator credentials or insufficient permissions.';
        }
      }

      if (authenticated) {
        navigate('/admin', { replace: true });
      } else {
        setError(apiError || 'Invalid administrator credentials.');
      }
    } catch (err) {
      setError(err.message || 'Unable to sign in to Admin Backoffice.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-2xl bg-indigo-600/20 border border-indigo-400/30 flex items-center justify-center shadow-xl">
            <ShieldCheck className="w-9 h-9 text-amber-400" />
          </div>
        </div>
        <h1 className="mt-5 text-center text-3xl font-extrabold text-white tracking-tight">
          Admin Backoffice
        </h1>
        <p className="mt-2 text-center text-sm text-indigo-200">
          Best 3 in Tennis — Store Catalog, Order Fulfillment &amp; Tax Zone Management
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-2xl rounded-2xl sm:px-10 border border-gray-100">
          {error && (
            <div
              role="alert"
              className="mb-6 p-3.5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5 text-red-700 text-sm"
            >
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label
                htmlFor="admin-email"
                className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5"
              >
                Admin Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  id="admin-email"
                  type="email"
                  required
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="dimabot@gmail.com"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5"
              >
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  id="admin-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full py-3 px-4 bg-indigo-700 hover:bg-indigo-800 disabled:bg-indigo-400 text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-sm"
            >
              <ShieldCheck className="w-4 h-4" />
              {loading ? 'Verifying Administrator Access...' : 'Sign In to Backoffice'}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
            <Link
              to="/extras-shop"
              className="inline-flex items-center gap-1.5 text-indigo-700 hover:text-indigo-900 font-semibold"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Return to Shop
            </Link>
            <span>Role-verified JWT Security</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
